import React from "react";
import { Button, Section } from "@react-email/components";
import { tLayout } from "../../translations";
import { useReturnVariableValues } from "../../hooks/use-return-variable-values";
import { getThemeButtonStyle } from "../button/shared-button-styling";
import { useEmailThemeRadius } from "../../hooks/use-emails-theme-radius";
import { sectionToParent, useThemeContextualColors } from "../../hooks";
import type { ReturnStoreAddressBlockProps } from "./types";

export function ReturnStoreAddressEmailRenderer({
  theme,
  section,
  locale,
  defaultLocale,
}: ReturnStoreAddressBlockProps) {
  const t = tLayout(locale, defaultLocale);
  const store = useReturnVariableValues().getReturnStore();
  const buttonRadius = useEmailThemeRadius(theme, "button");
  const colors = useThemeContextualColors(theme, sectionToParent(section));
  const buttonStyle = getThemeButtonStyle({ variant: "default", theme, textColor: colors.text });

  if (!store) return null;

  const mapLabel = (() => {
    const v = t("returnStoreAddress.mapButton");
    return v === "returnStoreAddress.mapButton" ? "View on map" : v;
  })();

  return (
    <Section style={{ textAlign: "center" }}>
      <div
        style={{
          borderRadius: "8px",
          padding: "12px 16px",
          backgroundColor: "#f9fafb",
          display: "inline-block",
          border: "1px solid #e5e7eb",
          minWidth: "220px",
          marginBottom: "8px",
          fontSize: "14px",
          lineHeight: "1.5",
        }}
      >
        <div className="text-center" style={{ fontWeight: "bold", fontSize: "16px", marginBottom: "4px" }}>
          📍 {store.name}
        </div>
        {store.address1 && <div className="text-center">{store.address1}</div>}
        {store.address2 && <div className="text-center">{store.address2}</div>}
        {(store.zip || store.city) && (
          <div className="text-center">{[store.zip, store.city].filter(Boolean).join(" ")}</div>
        )}
        {store.country && <div className="text-center">{store.country}</div>}
        {/* Center via text-align (not flexbox): Gmail strips display:flex,
            which left-aligned the button. The button renders as an inline
            box, so text-align:center centers it across email clients. */}
        {store.mapUrl && (
          <div style={{ marginTop: "12px", textAlign: "center" }}>
            <Button
              href={store.mapUrl}
              className={buttonRadius}
              style={buttonStyle as React.CSSProperties}
            >
              {mapLabel}
            </Button>
          </div>
        )}
      </div>
    </Section>
  );
}
